import * as amplitude from '@amplitude/unified';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  connectionForMethod,
  DEFAULT_LOGIN_METHOD,
  getRememberedLoginMethod,
  LoginMethod,
  rememberLoginMethod,
  safeReturnTo,
} from 'lib/login-method';
import React, { useState } from 'react';
import { Link, Redirect, useLocation } from 'react-router-dom';

interface MethodCopy {
  lastUsed: string;
  primary: string;
  alternate: string;
}

const COPY: Record<LoginMethod, MethodCopy> = {
  code: {
    lastUsed: 'You last logged in with email.',
    primary: 'Email me a code',
    alternate: 'Sign in with password instead',
  },
  password: {
    lastUsed: 'You last logged in with a password.',
    primary: 'Continue with password',
    alternate: 'Email me a code instead',
  },
};

const otherMethod = (method: LoginMethod): LoginMethod =>
  method === 'code' ? 'password' : 'code';

function SignInPage () {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();
  const returnTo = safeReturnTo(new URLSearchParams(location.search).get('returnTo'));
  const [email, setEmail] = useState('');
  const rememberedMethod = getRememberedLoginMethod();
  const primaryMethod = rememberedMethod ?? DEFAULT_LOGIN_METHOD;
  const copy = COPY[primaryMethod];

  if (!isLoading && isAuthenticated) {
    return <Redirect to={returnTo} />;
  }

  const signIn = (method: LoginMethod) => {
    rememberLoginMethod(method);
    amplitude.track('login', { method });
    // The per-call `connection` overrides the provider-level default in
    // App.tsx; `login_hint` prefills the identifier on Universal Login so the
    // user isn't asked for their email twice.
    loginWithRedirect({
      authorizationParams: {
        connection: connectionForMethod(method),
        ...(email ? { login_hint: email } : {}),
      },
      appState: { returnTo },
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Both buttons submit the form (so Enter and the browser's own email
    // validation work for either); the clicked button carries the method.
    const submitter = (event.nativeEvent as SubmitEvent).submitter;
    const method = submitter?.getAttribute('data-method') as LoginMethod | null;
    signIn(method ?? primaryMethod);
  };

  return (
    <div className="min-h-screen bg-muted/40 p-8">
      <div className="mx-auto w-full max-w-md">
        <Card className="mt-8 flex flex-col items-center p-8 text-center">
          <img src="/assets/hilo-full-logo.png" alt="HiLo Analytics" className="mb-6 h-8" />
          <h3 className="mb-6 text-2xl font-semibold text-foreground">
            Sign in to HiLo Analytics
          </h3>
          {/* First visit has no subtitle: the primary button already says what happens. */}
          {rememberedMethod && (
            <p className="-mt-4 mb-6 text-sm text-muted-foreground">{copy.lastUsed}</p>
          )}

          <form onSubmit={handleSubmit} className="flex w-full flex-col gap-4 text-left">
            <div className="flex flex-col gap-2">
              <Label htmlFor="sign-in-email">Email</Label>
              <Input
                id="sign-in-email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                placeholder="you@company.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <Button type="submit" size="lg" data-method={primaryMethod}>
              {copy.primary}
            </Button>
            <Button
              type="submit"
              variant="link"
              size="sm"
              data-method={otherMethod(primaryMethod)}
              className="text-muted-foreground"
            >
              {copy.alternate}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            Don&apos;t have an account yet?{' '}
            <Link to="/sign-up" className="font-medium text-foreground underline">
              Sign up
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}

export default SignInPage;
